import { Autocomplete, Grid, TextField } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CompanyOptions from "modules/companies/CompanyOptions";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import { ProjectMap } from "generated/graphql";
import useCreateProposalFromProjectMap from "hooks/projectMaps/useCreateProposalFromProjectMap";
import Modal from "modules/Modal/Modal";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRoute } from "utils/routing";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";

export default function CreateProposalFromMapDialog({
  open,
  onClose,
  projectMap,
}: {
  open: boolean;
  onClose: () => void;
  projectMap: ProjectMap;
}) {
  const navigate = useNavigate();
  const [createProposalFromProjectMap, { loading: submitting, isSuccess }] =
    useCreateProposalFromProjectMap();

  const [companyId, setCompanyId] = useState<string>("");
  const [contactId, setContactId] = useState<string>("");
  const [stageName, setStageName] = useState(projectMap.name ?? "");
  const [triggerRefetch, setTriggerRefetch] = useState(false);

  const [getCompany, { company }] = useGetCompanyLazy();

  useEffect(() => {
    if (companyId) getCompany({ id: companyId });
  }, [getCompany, companyId]);

  useEffect(() => {
    if (open) {
      setCompanyId("");
      setContactId("");
      setStageName(projectMap.name ?? "");
    }
  }, [open, projectMap.name]);

  const handleSubmit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      if (!companyId || !contactId) return;

      const result = await createProposalFromProjectMap({
        variables: {
          companyId,
          contactId,
          projectMapId: projectMap.id,
          stageName: stageName || null,
        },
      });

      const success = isSuccess(result.data);
      if (success) {
        const proposalId = result.data?.createProposalFromProjectMap?.id;
        onClose();
        if (proposalId) {
          navigate(getRoute("proposals.draft", [["proposalId", proposalId]]));
        }
      }
    },
    [
      companyId,
      contactId,
      projectMap.id,
      stageName,
      createProposalFromProjectMap,
      isSuccess,
      navigate,
      onClose,
    ]
  );

  return (
    <Modal open={open} onClose={onClose} styles={{ width: "560px", height: "auto" }}>
      <MDBox p={2}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          Create Proposal from Map
        </MDTypography>

        <MDBox component="form" role="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <CompanyOptions
              triggerRefetch={triggerRefetch}
              selectedCompany={company}
              setTriggerRefetch={setTriggerRefetch}
            >
              {({ companyOptions, companyContactOptions, loading, search, pagination }) => {
                const contactOptions = companyId ? companyContactOptions[companyId] ?? [] : [];

                return (
                  <>
                    <Grid item xs={12}>
                      <ScrollAsyncAutocomplete
                        value={companyOptions?.find((x) => x.value === companyId) ?? null}
                        options={companyOptions ?? []}
                        loading={loading}
                        label="Select Client"
                        search={search}
                        onLoadMore={pagination.loadMore}
                        hasMore={pagination.hasNextPage}
                        onChange={(value) => {
                          setCompanyId(value != null ? String(value) : "");
                          setContactId("");
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={contactOptions as any[]}
                        value={contactOptions.find((o: any) => o?.value === contactId) ?? null}
                        loading={loading}
                        disabled={!companyId}
                        isOptionEqualToValue={(opt: any, val: any) => opt?.value === val?.value}
                        getOptionLabel={(opt: any) => opt?.label ?? ""}
                        onChange={(_, option: any) => setContactId(option?.value ?? "")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="outlined"
                            label={`Select Contact ${companyId ? "" : "(Select Client First)"}`}
                          />
                        )}
                      />
                    </Grid>
                  </>
                );
              }}
            </CompanyOptions>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Stage Name"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
              />
            </Grid>
          </Grid>

          <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              type="submit"
              disabled={!companyId || !contactId || submitting}
            >
              {submitting ? "Creating..." : "Create Proposal"}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}
